import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partsApi } from './service';
import type { PartCreateValues, PartUpdateValues } from './schema';
import type { Part } from './types';

/**
 * Query key factory — one place to change the cache layout.
 *
 *   partsKeys.all                  every cached parts query
 *   partsKeys.lists()              all list queries (no params)
 *   partsKeys.list(filters)        a list with specific filters
 *   partsKeys.detail(id)           a single part
 */
export const partsKeys = {
  all: ['parts'] as const,
  lists: () => [...partsKeys.all, 'list'] as const,
  list: (filters: { partTypeId?: string } = {}) =>
    [...partsKeys.lists(), filters] as const,
  detail: (id: string) => [...partsKeys.all, 'detail', id] as const,
};

export function useParts(filters: { partTypeId?: string } = {}) {
  return useQuery({
    queryKey: partsKeys.list(filters),
    queryFn: () => partsApi.list(filters),
  });
}

export function usePart(id: string | undefined) {
  return useQuery({
    queryKey: id ? partsKeys.detail(id) : partsKeys.all,
    queryFn: () => partsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreatePart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PartCreateValues) => partsApi.create(input),
    onSuccess: (created: Part) => {
      qc.invalidateQueries({ queryKey: partsKeys.lists() });
      qc.setQueryData(partsKeys.detail(created.id), created);
    },
  });
}

export function useUpdatePart(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PartUpdateValues) => partsApi.update(id, patch),
    onSuccess: (updated: Part) => {
      qc.invalidateQueries({ queryKey: partsKeys.lists() });
      qc.setQueryData(partsKeys.detail(id), updated);
    },
  });
}

export function useDeletePart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partsApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: partsKeys.lists() });
      qc.removeQueries({ queryKey: partsKeys.detail(id) });
    },
  });
}
